// GA4 via Google Tag Manager, gated on Cookiebot "statistics" consent.
//
// The container is injected only after consent, so no request reaches Google
// before the user answers the dialog. This replaces GTM's standard <head>
// snippet; the noscript iframe is deliberately omitted, as it would run outside
// any consent mechanism. See utils/cookieConsent.ts.

import { hasConsent, onConsentChange } from "@/utils/cookieConsent"

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

const push = (payload: unknown) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

let gtmLoaded = false

// gtag commands must be pushed as an Arguments object (as in Google's official
// snippet) — gtag.js silently ignores plain arrays.
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  push(arguments)
}

// Consent Mode v2. GTM only loads with "statistics", but marketing may still be
// declined, so the per-category state is passed explicitly for any future tags.
const pushConsentState = () => {
  const marketing = hasConsent("marketing") ? "granted" : "denied"

  gtag("consent", "update", {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: hasConsent("statistics") ? "granted" : "denied",
    personalization_storage: hasConsent("preferences") ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  })
}

const injectGtm = () => {
  if (gtmLoaded) return
  gtmLoaded = true

  push({ "gtm.start": Date.now(), event: "gtm.js" })

  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  // Categorised for Cookiebot's scanner; consent is already granted here.
  script.setAttribute("data-cookieconsent", "statistics")
  document.head.appendChild(script)
}

// GA sets _ga / _ga_<stream> on the registrable domain. Drop them on withdrawal
// rather than leaving them to expire, so the opt-out takes effect immediately.
const clearGaCookies = () => {
  const { hostname } = window.location
  const domains = [undefined, hostname, `.${hostname}`, `.${hostname.split(".").slice(-2).join(".")}`]

  document.cookie.split("; ").forEach((entry) => {
    const name = entry.split("=")[0]

    if (!name.startsWith("_ga")) return

    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`
    })
  })
}

/**
 * Loads GTM once "statistics" consent is given. Withdrawing consent clears the GA
 * cookies and reloads — GTM/GA cannot be torn down at runtime. Returns an
 * unsubscribe function.
 */
export const initAnalytics = (): (() => void) => {
  if (typeof window === "undefined" || !GTM_ID) return () => {}

  return onConsentChange(() => {
    if (!hasConsent("statistics")) {
      if (gtmLoaded) {
        clearGaCookies()
        window.location.reload()
      }

      return
    }

    pushConsentState()
    injectGtm()
  })
}

/** Page view for a client-side navigation. Handled by the `spa_page_view` trigger in GTM. */
export const trackPageView = (path: string) => {
  if (!gtmLoaded) return

  push({ event: "spa_page_view", page_path: path, page_title: document.title })
}

export const trackEvent = (name: string, params: Record<string, unknown> = {}) => {
  if (!gtmLoaded) return

  push({ event: name, ...params })
}
